import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { quickbooksApi, QuickBooksAccount } from '../../api';

interface TrustAccountSelectionDialogProps {
  clientId: string;
  visible: boolean;
  onHide: () => void;
  onAccountSelected: (accountId: string) => void;
}

export const TrustAccountSelectionDialog: React.FC<TrustAccountSelectionDialogProps> = ({
  clientId,
  visible,
  onHide,
  onAccountSelected,
}) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<QuickBooksAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<QuickBooksAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadAccounts();
    }
  }, [visible, clientId]);

  const loadAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await quickbooksApi.fetchAccounts(clientId);
      setAccounts(response.accounts.filter(acc => acc.active));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load accounts from QuickBooks');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedAccount) return;

    setSaving(true);
    try {
      await quickbooksApi.setTrustAccountLiabilities(clientId, selectedAccount.id);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Trust Account Liabilities set to "${selectedAccount.name}"`,
        life: 3000,
      });
      onAccountSelected(selectedAccount.id);
      onHide();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to save Trust Account Liabilities',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const footer = (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="ms-out ms-out-close"
        text
        onClick={onHide}
        disabled={saving}
      />
      <Button
        label="Save"
        icon="ms-out ms-out-check"
        className="p-button-success"
        onClick={handleSave}
        disabled={!selectedAccount || saving}
        loading={saving}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Select Trust Account Liabilities"
        visible={visible}
        onHide={onHide}
        className="trust-account-dialog"
        modal
        footer={footer}
      >
        <p className="dialog-description">
          Select the QuickBooks account to use for Trust Account Liabilities when syncing deposits and purchases.
        </p>

        {error && <Message severity="error" text={error} className="dialog-error-message" />}

        <DataTable
          value={accounts}
          loading={loading}
          selectionMode="single"
          selection={selectedAccount}
          onSelectionChange={(e) => setSelectedAccount(e.value as QuickBooksAccount)}
          dataKey="id"
          scrollable
          scrollHeight="400px"
          size="small"
          stripedRows
          emptyMessage="No accounts found"
        >
          <Column selectionMode="single" style={{ width: '3rem' }} />
          <Column field="name" header="Account Name" sortable />
          <Column field="account_type" header="Type" sortable style={{ width: '15%' }} />
          <Column field="account_sub_type" header="Sub Type" style={{ width: '15%' }} />
          <Column
            field="current_balance"
            header="Balance"
            style={{ width: '15%' }}
            body={(rowData) => formatCurrency(rowData.current_balance)}
          />
        </DataTable>
      </Dialog>
    </>
  );
};
